import { colors, util } from "@/common/theme";
import { events } from "@/common/constants";
import NineGLImageElement from "@/components/gl/NineGLImage/NineGLImageElement";
import { setTypography } from "@/mono/lib/styles/mixins";
import { useGlobalStore } from "@/mono/store/global";
import emitter from "@/mono/lib/emitter";
import SplitText from "mono/components/SplitText";
import useEvent from "mono/lib/hooks/useEvent";
import { nextFrames } from "@/lib/pageReady";
import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

export default function ProjectHero({
  project: { title, coverImage, mobileCoverImage },
  isProject = true,
}) {
  const track = useRef();
  const titleRef = useRef();
  const glImage = useRef();
  const noWebGLImages = useGlobalStore((s) => s.noWebGLImages);
  const isMobileLayout = useGlobalStore((s) => s.isMobileLayout);
  const [isLoading, setIsLoading] = useState(!noWebGLImages);

  const textureReadyRef = useRef(false);
  const transitionCompleteRef = useRef(false);
  const hasAnimatedRef = useRef(false);

  async function tryAnimateIn() {
    if (hasAnimatedRef.current) return;
    if (!transitionCompleteRef.current) return;
    if (!noWebGLImages && !textureReadyRef.current) return;
    hasAnimatedRef.current = true;

    setIsLoading(false);
    glImage.current?.animateIn?.();

    if (noWebGLImages) {
      const ready = titleRef.current?.isReady?.();
      if (ready && typeof ready.then === "function") await ready;
      titleRef.current?.animateIn?.({ stagger: 0.04 });
    } else {
      setTimeout(() => {
        titleRef.current?.animateIn?.({ stagger: 0.04 });
      }, 200);
    }
  }

  useEffect(() => {
    if (!noWebGLImages) return;
    emitter.emit("PROJECT:PAGE_READY");
    transitionCompleteRef.current = true;
    nextFrames(5).then(() => tryAnimateIn());
  }, [noWebGLImages]);

  useEvent(events.LOADING_OUT_COMPLETE, () => {
    transitionCompleteRef.current = true;
    tryAnimateIn();
  });

  useEvent("PROJECT:ON_ENTERING", ({ from }) => {
    if (from && !noWebGLImages) {
      transitionCompleteRef.current = true;
      tryAnimateIn();
    }
  });

  const activeAsset = isMobileLayout ? mobileCoverImage : coverImage;
  const video = activeAsset?.video;
  const src = video?.mp4Url || video?.streamingUrl || activeAsset?.url;
  const isVideo = Boolean(video?.mp4Url || video?.streamingUrl || src?.includes(".mp4"));

  if (!src) return null;

  return (
    <Wrapper ref={track}>
      <Spinner className={isLoading ? "visible" : ""} />
      <NineGLImageElement
        ref={glImage}
        className="project-hero__gl"
        isVideo={isVideo}
        src={src}
        video={video}
        animateOnScroll={false}
        useHover={false}
        darken={0.2}
        onTextureReady={() => {
          textureReadyRef.current = true;
          emitter.emit("PROJECT:PAGE_READY");
          tryAnimateIn();
        }}
      />
      {isProject && (
        <SplitText
          tag="h1"
          ref={titleRef}
          animateOnScroll={false}
          animation="charClipped"
          type="chars"
          dangerouslySetInnerHTML={{ __html: title }}
        >
          {title}
        </SplitText>
      )}
    </Wrapper>
  );
}

const spin = keyframes`
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
`;

const Spinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border: 4px solid transparent;
  border-top-color: ${colors.aquaMarine};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  opacity: 0;
  transition: opacity 0.3s ease-out;
  pointer-events: none;
  z-index: 1;

  &.visible {
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  position: relative;
  ${util.heroSizing}

  h1 {
    position: absolute;
    top: 45%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${colors.aquaMarine};
    text-align: center;
    ${setTypography("heroText")}
    z-index: 3;
  }

  .project-hero__gl {
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 2;
  }

  .project__hero-image {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    visibility: hidden;
    height: 100%;
  }
`;
